import Link from "next/link";
import type { QuietGoodWeek } from "@/lib/conditions/busyness-data";
import { pressureLabel } from "@/lib/conditions/busyness-pressure";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";

type Props = {
  weeks: QuietGoodWeek[];
  type: "mountain" | "water";
  selectedLocation?: string;
};

function scorePillColour(score: number): string {
  if (score >= 5) return "bg-emerald-500";
  if (score >= 4) return "bg-lime-500";
  return "bg-amber-400";
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00Z");
  const e = new Date(end + "T00:00:00Z");
  const startStr = s.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  const endStr = e.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return `${startStr} – ${endStr}`;
}

export default function QuietestWeeksCallout({ weeks, type, selectedLocation }: Props) {
  if (weeks.length === 0) return null;

  const activity = type === "mountain" ? "mountain" : "water";

  return (
    <section>
      <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy mb-1">
        Quietest good weeks
      </h2>
      <p className="text-[13px] text-text-muted mb-5">
        {selectedLocation
          ? "Best weeks with good conditions and low crowd pressure for this destination."
          : `Good ${activity} conditions with low crowd pressure across all destinations. The sweet spot.`}
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {weeks.slice(0, 5).map((w) => (
          <Link
            key={`${w.locationSlug}-${w.weekStart}`}
            href={`/conditions/${w.locationSlug}`}
            className="block bg-white rounded-[14px] border border-border-light p-4 transition-all hover:-translate-y-0.5 hover:border-gold/30 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)]"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold text-navy">
                {formatDateRange(w.weekStart, w.weekEnd)}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-[11px] font-semibold text-white ${scorePillColour(w.timingScore)}`}
              >
                {w.timingScore}/5
              </span>
            </div>

            <h3 className="font-serif text-[15px] font-semibold text-navy truncate">
              {w.locationName}
            </h3>
            <p className="text-[11.5px] text-text-muted mt-1">
              {w.country}
            </p>

            <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-border-light">
              <div className="text-[11px]">
                <span className="text-text-muted">Crowd: </span>
                <span className="font-semibold text-navy">{Math.round(w.crowdPressure * 100)}%</span>
                <span className="text-text-muted ml-1">({pressureLabel(w.crowdPressure)})</span>
              </div>
              <div className="text-[11px]">
                <span className="text-text-muted">Conditions: </span>
                <span className="font-semibold text-navy">{scoreOutOf100Label(w.conditionsScore ?? 0)}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
